import { Helmet } from "react-helmet";
import { FaLinkedinIn, FaGithub, FaEnvelope } from "react-icons/fa";
import { useEffect } from "react";
import { getCalApi } from "@calcom/embed-react";

// Contact card component
const ContactCard = ({ icon: Icon, title, value, href }) => (
  <a
    href={href}
    target={href.startsWith('mailto:') ? '_self' : '_blank'}
    rel="noopener noreferrer"
    className="contact-card"
  >
    <div className="contact-card-icon">
      <Icon size={20} />
    </div>
    <div className="contact-card-content">
      <span className="contact-card-title">{title}</span>
      <span className="contact-card-value">{value}</span>
    </div>
  </a>
);

// Booking card component
const BookingCard = ({ duration, title, description, calLink, namespace }) => {
  return (
    <button
      className="booking-card"
      data-cal-namespace={namespace}
      data-cal-link={calLink}
      data-cal-config='{"theme":"dark"}'
    >
      <div className="booking-card-header">
        <span className="booking-card-title">{title}</span>
        <span className="booking-card-duration">{duration}</span>
      </div>
      <span className="booking-card-desc">{description}</span>
      <span className="booking-card-select">Click to book</span>
    </button>
  );
};

const AuthContact = () => {
  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "15min" });
      cal("ui", { theme: "dark" });
    })();
    (async function () {
      const cal = await getCalApi({ namespace: "30min" });
      cal("ui", { theme: "dark" });
    })();
    (async function () {
      const cal = await getCalApi({ namespace: "60min" });
      cal("ui", { theme: "dark" });
    })();
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact - limkc</title>
        <meta name="description" content="Get in touch with me for cloud architecture, AI automation, and consulting." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="w-full h-full overflow-auto flex justify-center">
        <div
          className="contact-page"
          style={{
            paddingTop: "calc(var(--navbar-height) + 2rem)",
          }}
        >
          {/* Header */}
          <h1 className="contact-header">Contact</h1>
          <p className="contact-subtitle">Let's connect.</p>

          {/* Intro text */}
          <p className="contact-intro">
            Connect with me through any of these platforms.
          </p>

          {/* Contact Cards Grid */}
          <div className="contact-grid">
            <ContactCard
              icon={FaEnvelope}
              title="Email"
              value="contact@limkc.com"
              href="mailto:contact@limkc.com"
            />
            <ContactCard
              icon={FaLinkedinIn}
              title="LinkedIn"
              value="in/kai-lim"
              href="https://www.linkedin.com/in/kai-lim-b1967a355/"
            />
            <ContactCard
              icon={FaGithub}
              title="GitHub"
              value="limkcreply"
              href="https://github.com/limkcreply"
            />
          </div>

          {/* Book a Call Section */}
          <div className="booking-section">
            <h2 className="booking-header">Book a Call</h2>
            <p className="booking-intro">
              Schedule a call with me if you need a more in-depth conversation about anything you want!
            </p>

            {/* Booking Cards */}
            <div className="booking-grid">
              <BookingCard
                duration="15 min"
                title="15 Min Chat"
                description="Quick chat"
                calLink="kai-koxdt1/15min"
                namespace="15min"
              />
              <BookingCard
                duration="30 min"
                title="30 Min Chat"
                description="Standard meeting"
                calLink="kai-koxdt1/30min"
                namespace="30min"
              />
              <BookingCard
                duration="60 min"
                title="60 Min Chat"
                description="In-depth conversation"
                calLink="kai-koxdt1/in-depth"
                namespace="60min"
              />
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .contact-page {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1.5rem 3rem;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .contact-header {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .contact-subtitle {
          font-size: 1.125rem;
          color: var(--text-secondary);
          margin-bottom: 2rem;
        }

        .contact-intro {
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
        }

        /* Contact Cards Grid */
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 3rem;
          width: 100%;
        }

        .contact-card {
          background: var(--glass-bg);
          border: 1px solid var(--border-color, rgba(255,255,255,0.1));
          border-radius: var(--radius-xl, 12px);
          padding: 0.75rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          cursor: pointer;
          transition: border-color 0.2s, transform 0.2s;
          text-decoration: none;
          text-align: left;
        }

        .contact-card:hover {
          border-color: var(--text-secondary);
          transform: translateY(-2px);
        }

        .contact-card-icon {
          color: var(--text-secondary);
        }

        .contact-card-content {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }

        .contact-card-title {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.95rem;
        }

        .contact-card-value {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        /* Booking Section */
        .booking-section {
          margin-bottom: 3rem;
          width: 100%;
        }

        .booking-header {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .booking-intro {
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
        }

        /* Booking Cards Grid */
        .booking-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          width: 100%;
        }

        .booking-card {
          background: transparent;
          border: 1px solid var(--border-color, rgba(255,255,255,0.1));
          border-radius: var(--radius-xl, 12px);
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          cursor: pointer;
          transition: border-color 0.2s, transform 0.2s;
          text-align: left;
        }

        .booking-card:hover {
          border-color: var(--text-secondary);
          transform: translateY(-2px);
        }

        .booking-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .booking-card-title {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 1rem;
        }

        .booking-card-duration {
          font-size: 0.75rem;
          color: var(--text-secondary);
          background: rgba(255,255,255,0.1);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .booking-card-desc {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        .booking-card-select {
          color: var(--text-secondary);
          font-size: 0.85rem;
          margin-top: auto;
          padding-top: 0.5rem;
          text-align: center;
        }

        .booking-card:hover .booking-card-select {
          color: var(--text-primary);
        }

        /* Footer */
        .contact-footer {
          margin-top: auto;
          padding-top: 3rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          border-top: 1px solid var(--border-color, rgba(255,255,255,0.1));
          width: 100%;
        }

        .social-icons {
          display: flex;
          gap: 1.5rem;
        }

        .social-icon {
          color: var(--text-secondary);
          transition: color 0.2s;
        }

        .social-icon:hover {
          color: var(--text-primary);
        }

        .copyright {
          color: var(--text-secondary);
          font-size: 0.85rem;
          opacity: 0.7;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .contact-header {
            font-size: 2rem;
          }

          .contact-grid {
            grid-template-columns: 1fr;
          }

          .booking-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .contact-page {
            padding: 0 1rem calc(var(--footer-height-mobile-portrait) + 2rem);
          }

          .contact-header {
            font-size: 1.75rem;
          }
        }

        /* Tablet (iPad Mini 744px - 1024px) */
        @media (min-width: 700px) and (max-width: 1024px) {
          .contact-page {
            padding-bottom: 8rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default AuthContact;
